import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

/**
 * Text messages (Settings → Text messages). The browser never sees the Twilio
 * Auth Token after it's saved: settings come from get_org_sms_settings (a safe
 * summary), and connect / disconnect / test go through the sms-settings edge
 * function, which keeps the token in Vault.
 */

export interface SmsSettings {
  connected: boolean;
  planAllows: boolean;
  accountSidLast4: string | null;
  accountName: string | null;
  senderKind: 'phone' | 'messaging_service' | 'alphanumeric' | null;
  senderValue: string | null;
  sendBookingConfirmation: boolean;
  sendAppointmentReminder: boolean;
  sendQuoteLink: boolean;
  connectedAt: string | null;
}

export interface SmsLogEntry {
  id: string;
  message_type: string;
  to_masked: string;
  status: string;
  error_code: string | null;
  error_message: string | null;
  created_at: string;
}

export interface SmsFunctionResult {
  ok: boolean;
  code?: string;
  message?: string;
  sid?: string;
  trialAccount?: boolean;
}

type RpcError = { message: string; code?: string };

// The generated Supabase types don't include these tables/RPCs yet.
type SmsDb = {
  rpc: (fn: string, args: Record<string, unknown>) => Promise<{ data: unknown; error: RpcError | null }>;
  from: (table: string) => {
    select: (columns: string) => {
      eq: (column: string, value: string) => {
        order: (column: string, opts: { ascending: boolean }) => {
          limit: (n: number) => Promise<{ data: unknown[] | null; error: RpcError | null }>;
        };
        maybeSingle: () => Promise<{ data: Record<string, unknown> | null; error: RpcError | null }>;
      };
    };
  };
  functions: {
    invoke: (fn: 'sms-settings', opts: { body: Record<string, unknown> }) => Promise<{ data: SmsFunctionResult | null; error: { message: string } | null }>;
  };
};

const db = supabase as unknown as SmsDb;

export const smsSettingsKey = (orgId: string | null | undefined) => ['sms-settings', orgId] as const;
export const smsLogKey = (orgId: string | null | undefined) => ['sms-log', orgId] as const;

interface SettingsRow {
  connected: boolean | null;
  plan_allows: boolean | null;
  account_sid_last4: string | null;
  account_name: string | null;
  sender_kind: SmsSettings['senderKind'];
  sender_value: string | null;
  send_booking_confirmation: boolean | null;
  send_appointment_reminder: boolean | null;
  send_quote_link: boolean | null;
  connected_at: string | null;
}

export function smsSettingsFromRow(row: SettingsRow | null | undefined): SmsSettings {
  return {
    connected: Boolean(row?.connected),
    planAllows: Boolean(row?.plan_allows),
    accountSidLast4: row?.account_sid_last4 ?? null,
    accountName: row?.account_name ?? null,
    senderKind: row?.sender_kind ?? null,
    senderValue: row?.sender_value ?? null,
    sendBookingConfirmation: row?.send_booking_confirmation !== false,
    sendAppointmentReminder: row?.send_appointment_reminder !== false,
    sendQuoteLink: row?.send_quote_link !== false,
    connectedAt: row?.connected_at ?? null,
  };
}

export function useSmsSettings(orgId: string | null | undefined) {
  return useQuery({
    queryKey: smsSettingsKey(orgId),
    enabled: Boolean(orgId),
    queryFn: async (): Promise<SmsSettings> => {
      const { data, error } = await db.rpc('get_org_sms_settings', { _org: orgId });
      if (error) throw error;
      const row = (Array.isArray(data) ? data[0] : data) as SettingsRow | undefined;
      return smsSettingsFromRow(row);
    },
  });
}

export function useSmsLog(orgId: string | null | undefined, enabled = true) {
  return useQuery({
    queryKey: smsLogKey(orgId),
    enabled: Boolean(orgId) && enabled,
    refetchInterval: 30_000,
    queryFn: async (): Promise<SmsLogEntry[]> => {
      const { data, error } = await db
        .from('sms_messages')
        .select('id, message_type, to_masked, status, error_code, error_message, created_at')
        .eq('organization_id', orgId!)
        .order('created_at', { ascending: false })
        .limit(25);
      if (error) throw error;
      return (data ?? []) as SmsLogEntry[];
    },
  });
}

async function invokeSms(body: Record<string, unknown>): Promise<SmsFunctionResult> {
  const { data, error } = await db.functions.invoke('sms-settings', { body });
  if (error) throw new Error(error.message || 'Something went wrong. Please try again.');
  if (!data) throw new Error('Something went wrong. Please try again.');
  return data;
}

/** A business outcome the page shows inline (wrong token, plan, etc.). */
export class SmsActionError extends Error {
  code?: string;
  constructor(message: string, code?: string) {
    super(message);
    this.code = code;
  }
}

function assertOk(result: SmsFunctionResult): SmsFunctionResult {
  if (!result.ok) throw new SmsActionError(result.message || 'That didn’t work. Please try again.', result.code);
  return result;
}

export interface ConnectSmsInput {
  accountSid: string;
  authToken: string;
  senderKind: 'phone' | 'messaging_service' | 'alphanumeric';
  senderValue: string;
}

export function useConnectSms(orgId: string | null | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: ConnectSmsInput) =>
      assertOk(await invokeSms({ action: 'connect', organizationId: orgId, ...input })),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: smsSettingsKey(orgId) }),
  });
}

export function useDisconnectSms(orgId: string | null | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => assertOk(await invokeSms({ action: 'disconnect', organizationId: orgId })),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: smsSettingsKey(orgId) }),
  });
}

export function useSendTestSms(orgId: string | null | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (to: string) => assertOk(await invokeSms({ action: 'test', organizationId: orgId, to })),
    onSettled: () => queryClient.invalidateQueries({ queryKey: smsLogKey(orgId) }),
  });
}

export interface SmsPreferences {
  sendBookingConfirmation: boolean;
  sendAppointmentReminder: boolean;
  sendQuoteLink: boolean;
}

export function useUpdateSmsPreferences(orgId: string | null | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (prefs: SmsPreferences) => {
      const { error } = await db.rpc('update_org_sms_preferences', {
        _org: orgId,
        _send_booking_confirmation: prefs.sendBookingConfirmation,
        _send_appointment_reminder: prefs.sendAppointmentReminder,
        _send_quote_link: prefs.sendQuoteLink,
      });
      if (error) throw error;
    },
    onMutate: async (prefs) => {
      await queryClient.cancelQueries({ queryKey: smsSettingsKey(orgId) });
      const previous = queryClient.getQueryData<SmsSettings>(smsSettingsKey(orgId));
      if (previous) queryClient.setQueryData<SmsSettings>(smsSettingsKey(orgId), { ...previous, ...prefs });
      return { previous };
    },
    onError: (_err, _prefs, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(smsSettingsKey(orgId), ctx.previous);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: smsSettingsKey(orgId) }),
  });
}

/** Whether a customer has opted out of texts (owners only; null if unknown). */
export function useCustomerSmsOptOut(customerId: string | null | undefined, enabled = true) {
  return useQuery({
    queryKey: ['customer-sms-opt-out', customerId],
    enabled: Boolean(customerId) && enabled,
    queryFn: async (): Promise<boolean | null> => {
      const { data, error } = await db.from('customers').select('sms_opt_out').eq('id', customerId!).maybeSingle();
      // Column missing (older database) or no access: hide the control.
      if (error || !data) return null;
      return Boolean(data.sms_opt_out);
    },
  });
}

export function useSetCustomerSmsOptOut(customerId: string | null | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (optOut: boolean) => {
      const { error } = await db.rpc('set_customer_sms_opt_out', { _customer_id: customerId, _opt_out: optOut });
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['customer-sms-opt-out', customerId] }),
  });
}
